<template>
  <paginated-table
    ref="table"
    endpoint="communityUsers"
    :sync-filters-with-url="syncFiltersWithUrl"
    :columns="columns"
    :load-relations="extraRelations"
    :extra-filters="extraFilters"
    v-bind="$attrs"
    :show-action-column="true"
    has-row-details
    @filters-changed="updateFilters"
    @updating="resetExports"
  >
    <template #head><slot name="head"></slot></template>
    <template #head-buttons><slot name="head-buttons"></slot></template>

    <template #row-details="{ item }">
      <community-user-details
        :community-user="item"
        :default-email-template="item.community.incorrect_proof_email_markdown"
        @deleted="refresh"
      />
    </template>

    <template v-if="showMailboxExport" #moreExports>
      <div class="mb-2">
        <file-download-link
          ref="mailbox-export-button"
          :api-url="`/communities/${communityId}/users/mailboxes`"
          :filename="$t('components.community.community_users_list.mailbox_list_filename')"
          :api-headers="{
            Accept: 'text/csv',
          }"
          :api-params="filters || {}"
          :button-label="$t('components.community.community_users_list.export_mailbox_list_csv')"
          variant="ghost-secondary"
          size="sm"
          download
        />
      </div>
      <div v-if="mailboxes">
        <icon-button
          variant="outline-success"
          size="sm"
          icon="files"
          @click.stop.prevent="copyMailboxes"
        >
          {{ $t("components.community.community_users_list.copy_mailbox_list") }}
        </icon-button>
        <b-form-textarea
          plaintext
          size="sm"
          class="mt-1"
          :value="mailboxes"
          rows="3"
        ></b-form-textarea>
      </div>
      <template v-else>
        <icon-button
          size="sm"
          variant="ghost-secondary"
          :loading="mailboxesLoading"
          @click.stop.prevent="exportMailboxes"
        >
          {{ $t("components.community.community_users_list.export_mailbox_list") }}
        </icon-button>
      </template>

      <div class="text-secondary small mt-1">
        {{ $t("components.community.community_users_list.mailbox_filter_note") }}
      </div>
    </template>
  </paginated-table>
</template>

<script>
import CommunityUserDetails from "@/components/Community/CommunityUserDetails.vue";
import FileDownloadLink from "@/components/shared/FileDownloadLink.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column, Filter } from "@/components/shared/PaginatedTableColumns";
import { capitalize, date } from "@/helpers/filters";
import { getCommunityAdminFilter } from "@/helpers/permissions/communities";
import { get } from "@/requests/server";

export default {
  name: "CommunityUsersList",
  components: {
    FileDownloadLink,
    IconButton,
    CommunityUserDetails,
    PaginatedTable,
  },
  props: {
    communityId: {
      type: Number,
      default: undefined,
    },
    hideFields: {
      type: Array,
      required: false,
      default: () => [],
    },
    showMailboxExport: {
      type: Boolean,
      default: false,
    },
    syncFiltersWithUrl: {
      type: Boolean,
      default: true,
    },
    userId: {
      type: Number,
      default: undefined,
    },
  },
  data() {
    return {
      extraRelations: [
        "user.identity_proof",
        "user.residency_proof",
        "user.avatar",
        "community",
        "custom_proof",
        "current_granted_subscription.",
        "current_granted_subscription.granted_by_user",
        "tags",
      ],
      mailboxes: null,
      mailboxesLoading: false,
      filters: {},
    };
  },
  computed: {
    columns() {
      return this.defaultColumns.filter((c) => !this.hideFields.includes(c.key));
    },
    defaultColumns() {
      return [
        new Column("id", capitalize(this.$t("lists.id")), "id", { showByDefault: false }),
        new Column("user_id", capitalize(this.$t("communities.fields.user.id")), "id", {
          showByDefault: false,
        }),
        Column.withRelation(
          "user.full_name",
          capitalize(this.$t("communities.fields.user.name")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: {
              ...getCommunityAdminFilter(this.$store.state.user, "read"),
              ...(this.communityId ? { "communities.id": this.communityId } : {}),
            },
          },
          "user_id",
          {
            urlFct: (item) => `/admin/users/${item.user_id}?community_id=${item.community_id}`,
          }
        ),
        new Column("community_id", capitalize(this.$t("communities.fields.community.id")), "id", {
          showByDefault: false,
        }),
        Column.withRelation(
          "community.name",
          capitalize(this.$t("communities.fields.community.name")),
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: {
              ...(this.userId ? { "users.id": this.userId } : {}),
              ...getCommunityAdminFilter(this.$store.state.user, "read", "id"),
            },
          },
          "community_id",
          {
            urlFct: (item) => `/admin/communities/${item.community_id}?user_id=${item.user_id}`,
          }
        ),
        new Column("user.email", capitalize(this.$t("users.fields.email")), "text"),
        new Column("user.phone", capitalize(this.$t("users.fields.phone")), "text", {
          showByDefault: false,
        }),
        new Column("updated_at", capitalize(this.$t("fields.updated_at")), "date"),
        new Column("user.address", capitalize(this.$t("users.fields.address")), "text", {
          showByDefault: false,
        }),
        new Column(
          "approved_at",
          capitalize(this.$t("communities.fields.user.approved_at")),
          "date",
          {
            showByDefault: false,
          }
        ),
        new Column(
          "suspended_at",
          capitalize(this.$t("communities.fields.user.suspended_at")),
          "date",
          {
            showByDefault: false,
          }
        ),
        Column.withSelect(
          "proof_state",
          capitalize(this.$t("communities.fields.user.proof")),
          [
            { value: null, label: capitalize(this.$t("communities.fields.user.proof_labels.all")) },
            {
              value: "absent",
              label: capitalize(this.$t("communities.fields.user.proof_labels.absent")),
              variant: "danger",
            },
            {
              value: "partial",
              label: capitalize(this.$t("communities.fields.user.proof_labels.partial")),
            },
            {
              value: "present",
              label: capitalize(this.$t("communities.fields.user.proof_labels.present")),
            },
            {
              value: "unevaluated",
              label: capitalize(this.$t("communities.fields.user.proof_labels.unevaluated")),
              variant: "warning",
            },
            {
              value: "valid",
              label: capitalize(this.$t("communities.fields.user.proof_labels.valid")),
              variant: "success",
            },
            {
              value: "invalid",
              label: capitalize(this.$t("communities.fields.user.proof_labels.invalid")),
              variant: "danger",
            },
          ],
          { sortable: false }
        ),
        Column.withSelect(
          "status",
          capitalize(this.$t("communities.fields.user.status")),
          [
            {
              value: null,
              label: capitalize(this.$t("communities.fields.user.status_labels.all")),
            },
            {
              value: "new",
              label: capitalize(this.$t("communities.fields.user.status_labels.new")),
              variant: "warning",
            },
            {
              value: "approved",
              label: capitalize(this.$t("communities.fields.user.status_labels.approved")),
              variant: "success",
            },
            {
              value: "suspended",
              label: capitalize(this.$t("communities.fields.user.status_labels.suspended")),
              variant: "danger",
            },
          ],
          { sortable: false }
        ),
        Column.withRelation(
          "approver.full_name",
          capitalize(this.$t("communities.fields.user.approver")),
          {
            relation: "users",
            label: "name",
            field: "id",
            params: { ...getCommunityAdminFilter(this.$store.state.user, "read") },
          },
          "approver_id",
          {
            urlFct: (item) => `/admin/users/${item.approver_id}`,
            showByDefault: false,
          }
        ),
        new Column(
          "approval_note",
          capitalize(this.$t("communities.fields.user.approval_note")),
          "text",
          {
            showByDefault: false,
          }
        ),
        Column.withSelect(
          "join_method",
          capitalize(this.$t("communities.fields.user.join_method")),
          [
            {
              value: null,
              label: capitalize(this.$t("communities.fields.user.join_method_labels.all")),
            },
            {
              value: "geo",
              label: capitalize(this.$t("communities.fields.user.join_method_labels.geo")),
              variant: "success",
            },
            {
              value: "manual",
              label: capitalize(this.$t("communities.fields.user.join_method_labels.manual")),
              variant: "warning",
            },
            {
              value: "invitation",
              label: capitalize(this.$t("communities.fields.user.join_method_labels.invitation")),
              variant: "primary",
            },
          ],
          { sortable: false, showByDefault: false }
        ),
        new Column(
          "current_granted_subscription.end_date",
          capitalize(this.$t("components.community.community_users_list.free_until")),
          "date",
          {
            formatter: date,
            showByDefault: false,
          }
        ),
      ];
    },
    extraFilters() {
      return [
        new Filter(
          "in_community_area",
          capitalize(
            this.$t("components.community.community_users_list.fields.in_community_area.title")
          ),
          "select",
          [
            {
              value: null,
              label: capitalize(
                this.$t(
                  "components.community.community_users_list.fields.in_community_area.options.null.all"
                )
              ),
            },
            {
              value: false,
              label: capitalize(
                this.$t(
                  "components.community.community_users_list.fields.in_community_area.options.false"
                )
              ),
            },
            {
              value: true,
              label: capitalize(
                this.$t(
                  "components.community.community_users_list.fields.in_community_area.options.true"
                )
              ),
            },
          ]
        ),
        new Filter(
          "tag",
          capitalize(this.$t("components.community.community_users_list.fields.tag.title")),
          "select",
          [
            {
              value: null,
              label: capitalize(
                this.$t("components.community.community_users_list.fields.tag.options.null.all")
              ),
            },
            {
              value: "volunteer",
              label: capitalize(
                this.$t("components.community.community_users_list.fields.tag.options.volunteer")
              ),
            },
          ]
        ),
      ];
    },
  },
  methods: {
    capitalize,
    refresh() {
      this.$refs.table.refresh();
    },
    resetExports() {
      if (this.$refs["mailbox-export-button"]) {
        this.$refs["mailbox-export-button"].reset();
      }
      this.mailboxes = null;
      this.mailboxesLoading = false;
    },
    setFilters(filters) {
      this.$refs.table.setFilters(filters);
    },
    async exportMailboxes() {
      if (this.mailboxes || this.mailboxesLoading) {
        return;
      }
      try {
        this.mailboxesLoading = true;
        const response = await get(`/communities/${this.communityId}/users/mailboxes`, {
          axiosRequestConfig: {
            params: this.filters || {},
          },
          notifications: {
            action: this.$t(
              "components.community.community_users_list.notifications.export_list.action"
            ),
          },
        });

        this.mailboxes = response.data;
      } finally {
        this.mailboxesLoading = false;
      }
    },
    copyMailboxes() {
      if (!this.mailboxes) {
        return;
      }

      navigator.clipboard.writeText(this.mailboxes).then(() => {
        this.$store.commit("addNotification", {
          title: this.$t("components.community.community_users_list.notifications.copy_list.title"),
          variant: "success",
        });
      });
    },
    updateFilters(filters) {
      this.filters = Object.assign({}, filters);
      this.mailboxes = null;
    },
  },
};
</script>

<style lang="scss">
@import "~bootstrap/scss/mixins/breakpoints";

.user-details-row {
  dl,
  dd {
    margin-bottom: 0;
  }
  dd + dt {
    margin-top: 0.5rem;
  }
}

@include media-breakpoint-down(xs) {
  .user-details-image {
    max-width: 16rem;
    margin-bottom: 1rem;
  }
}

.user-details-buttons {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}
</style>
